import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { LockableTabItem } from '@app/services/feature-access/feature-access.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import {
  ExtensionComponentType,
  ICheckoutExtensions,
  UrgencyBoostExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { generateRandomString } from '@utils/string-utils';
import { ViewMode } from '../../../models';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import {
  BoostGroupFormGroup,
  DEFAULT_BOOST_GROUP,
  DEFAULT_INTERNAL_DESCRIPTION,
  DEFAULT_INTERNAL_NAME,
  DEFAULT_SPACE_ABOVE_BADGE,
  LayoutFormGroup,
  UrgencyBoostFormGroup,
  UrgencyBoostTab,
  USAGE_TOKEN_PREFIX,
  USAGE_TOKEN_RANDOM_LENGTH,
  createBoostGroupFormGroup,
} from './model';

@Component({
  selector: 'urgency-boost',
  templateUrl: './urgency-boost.component.html',
  styleUrls: ['./urgency-boost.component.scss'],
  standalone: false,
})
export class UrgencyBoostComponent implements OnInit {
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  tabs: LockableTabItem<UrgencyBoostTab>[] = [
    { label: 'Settings', value: UrgencyBoostTab.SETTINGS },
    { label: 'Boosts', value: UrgencyBoostTab.BOOSTS },
    { label: 'Display Conditions', value: UrgencyBoostTab.DISPLAY_CONDITIONS },
  ];
  UrgencyBoostTab = UrgencyBoostTab;
  activeTab = UrgencyBoostTab.SETTINGS;
  formGroup: FormGroup<UrgencyBoostFormGroup>;
  displayRules = getExtensionRules();

  private destroyRef = inject(DestroyRef);
  // Emits before each form rebuild so previous valueChanges/statusChanges
  // subscriptions complete; takeUntilDestroyed handles the page-leave case.
  private formRebuild$ = new Subject<void>();

  constructor(
    private store: Store<CheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private featureAccess: FeatureAccessService,
  ) {}

  ngOnInit(): void {
    this.tabs = this.featureAccess.lockTabOnFreePlan(this.tabs, UrgencyBoostTab.DISPLAY_CONDITIONS);
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.URGENCY_BOOST);
    this.initForm();

    this.loadedExtension$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.updateFormValues(extension.template, extension.usageToken);
      }
    });

    this.extensionsService.save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.handleSave();
    });
  }

  /**
   * Wires the valueChanges + statusChanges streams to the live preview /
   * save-button validity. Called from initForm() so every new FormGroup
   * (including the one created when loaded extension data arrives async)
   * gets a fresh subscription. Without this, edits to the loaded form
   * never reach the preview because the original subscription was bound
   * to the empty FormGroup that was discarded.
   *
   * `formRebuild$` completes the prior streams on rebuild;
   * `takeUntilDestroyed` completes them when the user leaves the page.
   */
  private wireFormSubscriptions(): void {
    this.formRebuild$.next();

    this.formGroup.valueChanges
      .pipe(takeUntil(this.formRebuild$), takeUntilDestroyed(this.destroyRef))
      .subscribe((values) => this.dispatchExampleViewValues(values));

    this.formGroup.statusChanges
      .pipe(takeUntil(this.formRebuild$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.extensionsService.setFormValid(this.formGroup.valid));

    this.extensionsService.setFormValid(this.formGroup.valid);
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  onTabChange(event: UrgencyBoostTab): void {
    this.activeTab = event;
  }

  handleSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const rawValue = this.formGroup.getRawValue();
    const { usageToken, ...template } = rawValue;

    if (Array.isArray(template.boostGroups)) {
      for (const group of template.boostGroups) {
        if (group?.productsInCart?.mode === 'smart') {
          group.productsInCart.products = [];
        } else if (group?.productsInCart?.mode === 'manual') {
          group.productsInCart.rules = [];
        }
      }
    }

    const extensionId = this.loadedExtension?.id;
    const hasPendingIcons = this.hasPendingBadgeIcons(template);

    if (extensionId && hasPendingIcons) {
      this.extensionsService
        .uploadPendingBadgeIcons(extensionId, template)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (uploadedTemplate) => this.dispatchSave(extensionId, uploadedTemplate, usageToken),
          error: () =>
            this.extensionsService.createToast({
              mode: 'danger',
              message: 'Failed to upload custom icon. Please try again.',
            }),
        });
      return;
    }

    if (!extensionId && hasPendingIcons) {
      this.stripPendingBadgeIconFiles(template);
      this.extensionsService.createToast({
        mode: 'warning',
        message: 'Save your extension first, then re-open it to attach a custom icon.',
      });
    }

    this.dispatchSave(extensionId ?? null, template, usageToken);
  }

  private dispatchSave(extensionId: number | null, template: unknown, usageToken: string): void {
    const cleaned = JSON.parse(JSON.stringify(template));
    const payload: Partial<ICheckoutExtensions> = {
      template: cleaned,
      usageToken,
      type: ExtensionComponentType.URGENCY_BOOST,
    };
    if (extensionId) {
      this.store.dispatch(CheckoutActions.updateExtension({ id: extensionId, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  private hasPendingBadgeIcons(template: { boostGroups?: { badges?: { customIconUrl?: unknown }[] }[] }): boolean {
    const groups = template?.boostGroups;
    if (!Array.isArray(groups)) return false;
    return groups.some((g) => Array.isArray(g?.badges) && g.badges.some((b) => b?.customIconUrl instanceof File));
  }

  private stripPendingBadgeIconFiles(template: { boostGroups?: { badges?: { customIconUrl?: unknown }[] }[] }): void {
    const groups = template?.boostGroups;
    if (!Array.isArray(groups)) return;
    for (const g of groups) {
      for (const b of g?.badges ?? []) {
        if (b?.customIconUrl instanceof File) {
          (b as { customIconUrl?: unknown }).customIconUrl = null;
          // Drop the ephemeral blob: URL that pointed at the stripped file.
          if (
            typeof (b as { icon?: unknown }).icon === 'string' &&
            String((b as { icon?: unknown }).icon).startsWith('blob:')
          ) {
            (b as { icon?: unknown }).icon = '';
          }
        }
      }
    }
  }

  private dispatchExampleViewValues(values: unknown): void {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    this.store.dispatch(
      CheckoutActions.liveExampleTemplateView({
        data: {
          [ExtensionComponentType.URGENCY_BOOST]: { mode, template: values },
        },
      }),
    );
  }

  private initForm(loadedValues?: UrgencyBoostExtension.Template, usageToken?: string): void {
    this.formGroup = new FormGroup<UrgencyBoostFormGroup>({
      usageToken: new FormControl(usageToken ?? USAGE_TOKEN_PREFIX + generateRandomString(USAGE_TOKEN_RANDOM_LENGTH), {
        nonNullable: true,
      }),
      internalName: new FormControl(loadedValues?.internalName ?? DEFAULT_INTERNAL_NAME, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      internalDescription: new FormControl(loadedValues?.internalDescription ?? DEFAULT_INTERNAL_DESCRIPTION, {
        nonNullable: true,
      }),
      maxBadgesPerProduct: new FormControl<number | null>(
        loadedValues?.maxBadgesPerProduct ?? UrgencyBoostExtension.DEFAULT_MAX_BADGES_PER_PRODUCT,
        { validators: [Validators.min(1)] },
      ),
      layout: new FormGroup<LayoutFormGroup>({
        spaceAboveBadge: new FormControl(loadedValues?.layout?.spaceAboveBadge ?? DEFAULT_SPACE_ABOVE_BADGE, {
          nonNullable: true,
        }),
      }),
      boostGroups: new FormArray<FormGroup<BoostGroupFormGroup>>(
        (loadedValues?.boostGroups?.length ? loadedValues.boostGroups : [{ ...DEFAULT_BOOST_GROUP, id: 0 }]).map(
          (group, index) =>
            createBoostGroupFormGroup(
              index,
              group,
              (rules) =>
                this.rulesDynamicFormService.initRulesFormArray(rules, this.displayRules) as FormArray<
                  FormGroup<TypedRuleGroup>
                >,
            ),
        ),
      ),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.restrictionsRules ?? [],
        this.displayRules,
      ) as FormArray<FormGroup<TypedRuleGroup>>,
    });

    this.wireFormSubscriptions();
  }

  private updateFormValues(template?: UrgencyBoostExtension.Template, usageToken?: string): void {
    this.initForm(template, usageToken);
  }
}
